"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BackupClient = void 0;
const secretjs_1 = require("secretjs");
const curve25519_js_1 = require("curve25519-js");
const hkdf_1 = require("@noble/hashes/hkdf");
const sha512_1 = require("@noble/hashes/sha512");
const AesCrypt_1 = require("./AesCrypt");
const buffer_1 = require("buffer/");
const hmac_1 = require("@noble/hashes/hmac");
const KEY_LEN = 32;
const BLOCK_SIZE = 100;
const TX_OPTIONS = {
    gasLimit: 1000000,
};
class BackupClient {
    constructor(user, signer, contractAddr, contractHash, chainId, cryptoLib, isKeplr) {
        this.user = user;
        this.signer = signer;
        this.contractAddr = contractAddr;
        this.contractHash = contractHash;
        this.chainId = chainId;
        this.cryptoLib = cryptoLib;
        this.isKeplr = isKeplr;
        this.calcPadding = (obj, block) => {
            const surplus = JSON.stringify(obj).length % block;
            if (surplus == 0) {
                return 0;
            }
            const padding = block - surplus;
            obj.padding = "a".repeat(padding);
            return obj;
        };
    }
    async getBlockHeight() {
        const latestBlock = await this.user.query.tendermint.getLatestBlock({});
        const blockHeight = latestBlock.block?.header?.height;
        if (!blockHeight) {
            throw new Error("Unable to retrieve block height");
        }
        return blockHeight;
    }
    async decodeBytes(tx) {
        return (0, secretjs_1.fromUtf8)(secretjs_1.MsgExecuteContractResponse.decode(tx.data[0]).data);
    }
    async getShareCount() {
        const permit = await (0, secretjs_1.newPermit)(this.signer, this.user.address, this.chainId, "", [this.contractAddr], [], this.isKeplr);
        const latestBlock = await this.user.query.tendermint.getLatestBlock({});
        const blockHeight = latestBlock.block?.header?.height;
        if (!blockHeight) {
            throw new Error("Could not retrieve block height");
        }
        const res = await this.user.query.compute.queryContract({
            contract_address: this.contractAddr,
            code_hash: this.contractHash,
            query: {
                get_share_count: this.calcPadding({ permit, block_height: blockHeight, padding: "" }, BLOCK_SIZE),
            },
        });
        if (!res.shares) {
            throw new Error("Query error: " + res);
        }
        return res.shares;
    }
    async getBackupPrice() {
        const res = await this.user.query.compute.queryContract({
            contract_address: this.contractAddr,
            code_hash: this.contractHash,
            query: {
                get_backup_price: this.calcPadding({ padding: "" }, BLOCK_SIZE),
            },
        });
        if (!("price" in res)) {
            throw new Error("Query error: " + JSON.stringify(res));
        }
        return res.price;
    }
    async getRecoveryPrice() {
        const res = await this.user.query.compute.queryContract({
            contract_address: this.contractAddr,
            code_hash: this.contractHash,
            query: {
                get_recovery_price: this.calcPadding({ padding: "" }, BLOCK_SIZE),
            },
        });
        if (!("price" in res)) {
            throw new Error("Query error: " + JSON.stringify(res));
        }
        return res.price;
    }
    async registerBackup(email) {
        const price = await this.getBackupPrice();
        let { private: sec_a, public: pub_a } = (0, curve25519_js_1.generateKeyPair)(this.cryptoLib.getRandomValues(new Uint8Array(32)));
        let nonce_a = this.cryptoLib.getRandomValues(new Uint8Array(32));
        let registerTx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                register_backup: this.calcPadding({
                    email,
                    pub_a: buffer_1.Buffer.from(pub_a).toString("base64"),
                    nonce_a: buffer_1.Buffer.from(nonce_a).toString("base64"),
                    padding: "",
                }, BLOCK_SIZE),
            },
            sent_funds: price === 0 ? [] : [{ denom: "uscrt", amount: price.toString() }],
        }, TX_OPTIONS);
        if (registerTx.code !== 0) {
            console.log(registerTx);
            throw new Error("register backup failed - code " + registerTx.code + registerTx.rawLog);
        }
        const { tid, pub_c, nonce_c, } = JSON.parse(await this.decodeBytes(registerTx));
        const shared_sec = (0, curve25519_js_1.sharedKey)(sec_a, buffer_1.Buffer.from(pub_c, "base64"));
        const key = buffer_1.Buffer.from((0, hkdf_1.hkdf)(sha512_1.sha512, shared_sec, buffer_1.Buffer.concat([nonce_a, buffer_1.Buffer.from(nonce_c, "base64")]), undefined, KEY_LEN)).toString("base64");
        return { tid, key, hash: registerTx.transactionHash };
    }
    async checkCodeBackup(_key, code) {
        const key = buffer_1.Buffer.from(_key, "base64");
        const aesgcm = new AesCrypt_1.AesGcm(key, this.cryptoLib);
        const nonce_decrypt = await aesgcm.decrypt(buffer_1.Buffer.from(code, "base64"));
        return buffer_1.Buffer.from(nonce_decrypt).toString("base64");
    }
    async confirmBackup(tid, _key, nonce, passphrase, threshold, owners) {
        const key = buffer_1.Buffer.from(_key, "base64");
        const aesgcm = new AesCrypt_1.AesGcm(key, this.cryptoLib);
        const pp_info = {
            pp: passphrase,
            threshold,
            addr_list: owners,
        };
        const pp_info_encrypt = await aesgcm.encrypt(buffer_1.Buffer.from(JSON.stringify(pp_info), "utf-8"));
        const confirmTx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                confirm_backup: this.calcPadding({
                    tid,
                    pp_info_encrypt: buffer_1.Buffer.from(pp_info_encrypt).toString("base64"),
                    nonce,
                    padding: "",
                }, BLOCK_SIZE),
            },
        }, TX_OPTIONS);
        if (confirmTx.code !== 0)
            throw new Error("confirm backup failed - code " + confirmTx.code + confirmTx.rawLog);
        const { uuid } = JSON.parse(await this.decodeBytes(confirmTx));
        return { uuid, hash: confirmTx.transactionHash };
    }
    async registerTransfer(uuid) {
        let { private: sec_a, public: pub_a } = (0, curve25519_js_1.generateKeyPair)(this.cryptoLib.getRandomValues(new Uint8Array(32)));
        let nonce_a = this.cryptoLib.getRandomValues(new Uint8Array(32));
        const registerTransferTx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                register_transfer: this.calcPadding({
                    pub_a: buffer_1.Buffer.from(pub_a).toString("base64"),
                    nonce_a: buffer_1.Buffer.from(nonce_a).toString("base64"),
                    uuid,
                    padding: "",
                }, BLOCK_SIZE),
            },
        }, TX_OPTIONS);
        if (registerTransferTx.code !== 0) {
            throw new Error("register transfer failed - code " +
                registerTransferTx.code +
                registerTransferTx.rawLog);
        }
        const { tid, pub_c, nonce_c, } = JSON.parse(await this.decodeBytes(registerTransferTx));
        const shared_sec = (0, curve25519_js_1.sharedKey)(sec_a, buffer_1.Buffer.from(pub_c, "base64"));
        const key = buffer_1.Buffer.from((0, hkdf_1.hkdf)(sha512_1.sha512, shared_sec, buffer_1.Buffer.concat([nonce_a, buffer_1.Buffer.from(nonce_c, "base64")]), undefined, KEY_LEN)).toString("base64");
        return { tid, key, hash: registerTransferTx.transactionHash };
    }
    async checkCodeTransfer(code) {
        const splitCode = code.split("#");
        return { tid: splitCode[0], nonce: splitCode[1] };
    }
    async getTransferInfo(tid, nonce) {
        const res = await this.user.query.compute.queryContract({
            contract_address: this.contractAddr,
            code_hash: this.contractHash,
            query: {
                get_transfer_info: this.calcPadding({
                    tid,
                    nonce,
                    padding: "",
                }, BLOCK_SIZE),
            },
        });
        if (!res.Ok) {
            throw new Error("Query error: " + res);
        }
        return { uuid: res.Ok.uuid, addr: res.Ok.addr };
    }
    async confirmTransfer(tid, _key, to) {
        const key = buffer_1.Buffer.from(_key, "base64");
        const aesgcm = new AesCrypt_1.AesGcm(key, this.cryptoLib);
        const to_encrypt = buffer_1.Buffer.from(await aesgcm.encrypt(buffer_1.Buffer.from(to, "utf-8"))).toString("base64");
        const confirmTransferTx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                confirm_transfer: this.calcPadding({
                    tid,
                    to_encrypt,
                    padding: "",
                }, BLOCK_SIZE),
            },
        }, TX_OPTIONS);
        if (confirmTransferTx.code !== 0) {
            throw new Error("confirm transfer failed - code " +
                confirmTransferTx.code +
                confirmTransferTx.rawLog);
        }
        return { hash: confirmTransferTx.transactionHash };
    }
    async approveTransfer(tid, nonce, to) {
        const to_hmac = buffer_1.Buffer.from(hmac_1.hmac
            .create(sha512_1.sha512, buffer_1.Buffer.from(nonce, "base64"))
            .update(buffer_1.Buffer.from(to, "utf-8"))
            .digest()).toString("base64");
        const tx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                approve_transfer: this.calcPadding({
                    tid,
                    to_hmac,
                    padding: "",
                }, BLOCK_SIZE),
            },
        }, TX_OPTIONS);
        if (tx.code !== 0) {
            throw new Error("approve transfer failed - code " + tx.code + tx.rawLog);
        }
        return { hash: tx.transactionHash };
    }
    async registerRecovery(uuid) {
        const price = await this.getRecoveryPrice();
        let { private: sec_a, public: pub_a } = (0, curve25519_js_1.generateKeyPair)(this.cryptoLib.getRandomValues(new Uint8Array(32)));
        const nonce_a = this.cryptoLib.getRandomValues(new Uint8Array(32));
        const registerRecoverTx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                register_recovery: this.calcPadding({
                    uuid,
                    nonce_a: buffer_1.Buffer.from(nonce_a).toString("base64"),
                    pub_a: buffer_1.Buffer.from(pub_a).toString("base64"),
                    padding: "",
                }, BLOCK_SIZE),
            },
            sent_funds: price === 0 ? [] : [{ denom: "uscrt", amount: price.toString() }],
        }, TX_OPTIONS);
        if (registerRecoverTx.code !== 0) {
            throw new Error("register recovery failed - code " +
                registerRecoverTx.code +
                registerRecoverTx.rawLog);
        }
        const { tid, pub_c, nonce_c, } = JSON.parse(await this.decodeBytes(registerRecoverTx));
        const shared_sec = (0, curve25519_js_1.sharedKey)(sec_a, buffer_1.Buffer.from(pub_c, "base64"));
        let symKey = buffer_1.Buffer.from((0, hkdf_1.hkdf)(sha512_1.sha512, shared_sec, buffer_1.Buffer.concat([nonce_a, buffer_1.Buffer.from(nonce_c, "base64")]), undefined, KEY_LEN)).toString("base64");
        return { tid, key: symKey, hash: registerRecoverTx.transactionHash };
    }
    async checkCodeRecovery(_key, code) {
        const key = buffer_1.Buffer.from(_key, "base64");
        const aesgcm = new AesCrypt_1.AesGcm(key, this.cryptoLib);
        const nonce_decrypt = await aesgcm.decrypt(buffer_1.Buffer.from(code, "base64"));
        return buffer_1.Buffer.from(nonce_decrypt).toString("base64");
    }
    async confirmRecovery(tid, nonce, _key, remove) {
        const key = buffer_1.Buffer.from(_key, "base64");
        const aesgcm = new AesCrypt_1.AesGcm(key, this.cryptoLib);
        const confirmRecTx = await this.user.tx.compute.executeContract({
            code_hash: this.contractHash,
            contract_address: this.contractAddr,
            sender: this.user.address,
            msg: {
                confirm_recovery: this.calcPadding({
                    nonce,
                    tid,
                    delete: remove,
                    padding: "",
                }, BLOCK_SIZE),
            },
        }, TX_OPTIONS);
        if (confirmRecTx.code !== 0) {
            throw new Error("confirm recovery failed - code " +
                confirmRecTx.code +
                confirmRecTx.rawLog);
        }
        const { pp_encrypt } = JSON.parse(await this.decodeBytes(confirmRecTx));
        const pp_buff = buffer_1.Buffer.from(await aesgcm.decrypt(buffer_1.Buffer.from(pp_encrypt, "base64")));
        return {
            passphrase: pp_buff.toString("utf-8"),
            hash: confirmRecTx.transactionHash,
        };
    }
}
exports.BackupClient = BackupClient;
